import React, { useState } from 'react';
import './index.scss';
// import { RightOutlined } from '@ant-design/icons';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import arrowRightBlack from '../../assests/svg/arrow-right-black.svg';
import arrowRightHover from '../../assests/svg/arrow-right-hover.svg';
import NumArrow from '@/pages/intelligence/pages/TopCharts/components/DetailsPage/NumArrow';
import { indicatorValRankingFormat } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overview/PerformanceSummary/cardUtils';
import DragHandle from '@sg/assests/svg/draghandle-icon.svg';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import clsx from 'clsx';
import moment from 'moment';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { formatPercentNum, formatSmallValue, unitConversion } from '@/pages/dashboard/utils/utils';
import { getCardChart, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import { yAxisFormat } from '@/pages/dashboard/components/Chart/_config/utils';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';
import CommonBubble from '@home/components/CommonBubble';
import LockClose from '@sg/assests/svg/icon-no-access-lock.svg';

const NO_ACCESS_TEXT = 'No access';
const NO_ACCESS_TOOLTIP =
  'For access-related inquiries, please contact Irenejliang.';

interface MetricsBoardVerticalProps {
  title: string;
  num?: number | null; // Allow null to display "-"
  formatter?: string; // Formatter from API (formatRule for dataFormat)
  unit?: string; // Unit from API ('hour' | 'USD' | 'RMB')
  explain?: string; // Explain text for tooltip
  textList: Array<{ value: string; text: string }>;
  onHeaderClick?: () => void;
  mode?: 'normal' | 'preview' | 'edit'; // Add edit mode
  onEdit?: () => void; // Edit button callback
  onDelete?: () => void; // Delete button callback
  className?: string; // Additional CSS class
  style?: React.CSSProperties; // Inline styles
  trendData?: {
    xAxisData: string[];
    series: any[];
    metrics_name: string;
    granularity?: 'daily' | 'weekly' | 'monthly'; // Add granularity to determine date format
  }; // Trend chart data
  noAccess?: boolean;
}

const MetricsBoardVertical = ({
  title,
  num,
  formatter,
  unit,
  explain,
  textList,
  onHeaderClick,
  mode = 'normal', // Default to normal mode
  onEdit,
  onDelete,
  className,
  style,
  trendData,
  noAccess = false,
}: MetricsBoardVerticalProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatToOneDecimal = (value: string): string => {
    if (!value) return value;
    // Extract numeric part and any suffix (like %)
    const match = value.match(/^(-?)(\d+\.?\d*)(.*)$/);
    if (!match) return value;

    const sign = match[1]; // Negative sign if present
    const numStr = match[2]; // Numeric part
    const suffix = match[3]; // Suffix like %

    const num = parseFloat(numStr);
    if (isNaN(num)) return value;

    // Format to 1 decimal place
    const formatted = num.toFixed(1);
    return `${sign}${formatted}${suffix}`;
  };

  const getNumConfig = (text: string) => {
    if (!text) return null;
    // Use indicatorValRankingFormat to get the proper formatting
    const {
      _indicatorContent,
      _indicatorSize,
      _indicatorColor,
      _isIndicatorArrow,
      _indicatprsArrowColor,
    } = indicatorValRankingFormat(text);

    // Format the content to 1 decimal place
    const formattedContent = formatToOneDecimal(_indicatorContent);

    // Map the indicatorValRankingFormat output to NumArrow expected format
    return {
      numContent: formattedContent,
      numColor: _indicatorColor,
      numSize: _indicatorSize,
      arrow: _isIndicatorArrow,
      arrowColor: _indicatprsArrowColor,
    };
  };

  const wrapWithBubble = (element: React.ReactNode) => {
    if (!explain) return element;
    return (
      <CommonBubble content={<FormatHtmlTextToReact text={explain} />} placement="top">
        {element}
      </CommonBubble>
    );
  };

  const content = (
    <div className={clsx('dbs-metrics-board-vertical-wrapper', className)} style={style}>
      {/* Header */}
      <div
        className={`dbs-metrics-board-vertical-header ${mode === 'normal' ? 'hover-enabled' : 'hover-disabled'}`}
        onClick={mode === 'normal' ? onHeaderClick : undefined}
        onMouseEnter={mode === 'normal' ? () => setIsHovered(true) : undefined}
        onMouseLeave={mode === 'normal' ? () => setIsHovered(false) : undefined}
        style={{ cursor: mode === 'normal' ? 'pointer' : 'default' }}
      >
        <div className="dbs-metrics-board-vertical-title">
          {wrapWithBubble(<span>{title}</span>)}
        </div>

        {/* Action buttons based on mode */}
        {mode === 'normal' && (
          <img
            className="dbs-metrics-board-vertical-arrow"
            src={isHovered ? arrowRightHover : arrowRightBlack}
            alt="Arrow Right"
          />
        )}

        {mode === 'edit' && (
          <div className="dbs-metrics-board-vertical-actions">
            <EditButton
              className="dbs-metrics-board-vertical-edit-btn"
              onClick={() => onEdit?.()}
              title="Edit"
            />
            <DeleteButton
              className="dbs-metrics-board-vertical-delete-btn"
              onClick={() => onDelete?.()}
              title="Delete"
            />
          </div>
        )}
      </div>

      {/* Body: Value/Metrics on left, Chart on right */}
      <div className="dbs-metrics-board-vertical-body">
        {noAccess ? (
          <div className="dbs-metrics-board-vertical-no-access-full">
            <CommonBubble
              content={
                <span>{NO_ACCESS_TOOLTIP}</span>
              }
              placement="top"
              style={{ maxWidth: 'none' }}
            >
              <div className="dbs-metrics-board-vertical-no-access">
                <img src={LockClose} alt="No access" />
                <span>{NO_ACCESS_TEXT}</span>
              </div>
            </CommonBubble>
          </div>
        ) : (
          <>
            {/* Left side: Value and change metrics */}
            <div className="dbs-metrics-board-vertical-body-left">
              {/* Title Number */}
              <span className="dbs-metrics-board-vertical-num">
                {num !== undefined && num !== null && !isNaN(num)
                  ? (() => {
                    let formattedValue: string;

                    if (formatter) {
                      try {
                        // Special handling: if formatter is thousands2Decimal, use db_bm00 instead
                        // db_bm00: if value < 1000000, use '0,0.00', otherwise use 'db_bmk00'
                        const formatRule = (formatter === 'thousands2Decimal' || formatter === '0,0.00')
                          ? 'db_bm00'
                          : formatter;

                        const formatted = dataFormat({ value: num, formatRule });
                        // Check if dataFormat returned NaN or invalid value
                        if (formatted === null || formatted === undefined || formatted === 'NaN' || (typeof formatted === 'number' && isNaN(formatted))) {
                          formattedValue = (num !== null && num !== undefined) ? num.toLocaleString() : '-';
                        } else {
                          formattedValue = String(formatted);
                        }
                      } catch (error) {
                        // If dataFormat throws an error, fall back to toLocaleString
                        formattedValue = (num !== null && num !== undefined) ? num.toLocaleString() : '-';
                      }
                    } else {
                      formattedValue = (num !== null && num !== undefined) ? num.toLocaleString() : '-';
                    }

                    // Apply unit formatting
                    if (unit === 'hour') {
                      return `${formattedValue}H`;
                    } else if (unit === 'USD' || unit === 'RMB') {
                      return `$${formattedValue}`;
                    }

                    return formattedValue;
                  })()
                  : "-"}
              </span>
              {/* Small Label */}
              <div className="dbs-metrics-board-metrics-label">
                {textList.map((item, i) => {
                  const numArrowData = getNumConfig(item.text);

                  return (
                    <div className="dbs-metrics-board-vertical-text-flex" key={i}>
                      <div className="num-gray">{item.value}</div>
                      {numArrowData ? (
                        <NumArrow dataContent={numArrowData} type={'num'} />
                      ) : (
                        <span className="num-gray">-</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right side: trend chart */}
            {trendData && trendData.xAxisData.length > 0 && trendData.series.length > 0 && (
              <div className="dbs-metrics-board-vertical-body-right">
                <LineChart
                  height={75}
                  xAxisData={trendData.xAxisData}
                  series={trendData.series}
                  isShowLegend={false}
                  formatterFunc={(param: string | any[]) => {
                    const paramsArr = Array.isArray(param) ? param : [param];
                    // Format date based on granularity:
                    // - monthly: 'YYYY-MM' (e.g., "2025-07")
                    // - daily/weekly: 'YYYY-MM-DD' (e.g., "2025-07-01")
                    const dateFormat = trendData.granularity === 'monthly' ? 'YYYY-MM' : 'YYYY-MM-DD';
                    const date = moment(paramsArr[0]?.axisValue).format(dateFormat);
                    let returnStr = `<div class=${'basic-formatter-box'}>
                    <div class=${'basic-formatter-header'}>
                    <p class='basic-formatter-one basic-formatter-title'>${date}</p>
                    <p class='basic-formatter-two'>${title}</p></div>`;
                    for (let i = 0; i < paramsArr.length; i++) {
                      const paramValue = paramsArr[i].value;
                      // Use formatter from API if available, otherwise use simple formatting
                      let dauValue: string;

                      // Check if paramValue is null or undefined
                      if (paramValue === null || paramValue === undefined) {
                        dauValue = '-';
                      } else if (formatter) {
                        try {
                          const formatRule = (formatter === 'thousands2Decimal' || formatter === '0,0.00')
                            ? 'db_bm00'
                            : formatter;
                          const formatted = dataFormat({ value: paramValue, formatRule });
                          dauValue = (formatted === null || formatted === undefined || formatted === 'NaN' || (typeof formatted === 'number' && isNaN(formatted)))
                            ? paramValue.toLocaleString()
                            : String(formatted);
                        } catch (error) {
                          dauValue = paramValue.toLocaleString();
                        }
                      } else {
                        dauValue = paramValue.toLocaleString();
                      }

                      // Apply unit formatting to tooltip
                      if (unit === 'hour') {
                        dauValue = `${dauValue}H`;
                      } else if (unit === 'USD' || unit === 'RMB') {
                        dauValue = `$${dauValue}`;
                      }

                      const name = paramsArr[i].seriesName;
                      const radio = `<span style="display:inline-block;margin-right:4px;border-radius:8px;width:8px;height:8px;background-color:${paramsArr?.[i]?.color}"></span>`;
                      returnStr += `<div class=${'basic-formatter-header'}>
                      <p class=${'basic-formatter-one'}>${radio}&nbsp;${name}</p>
                      <p class=${'basic-formatter-two'}>${dauValue}</p></div>`;
                    }
                    returnStr = `${returnStr}</div>`;
                    return returnStr;
                  }}
                  setOtherConfigFunc={(opt: any) => {
                    opt = getCardChart(opt, { timeType: 'day' });
                    opt = xAxisHighLight(opt, {
                      lineStyle: {
                        type: 'solid',
                      },
                      formatter: (params: any) => {
                        return params?.value?.slice(-2); // Show day only (e.g., "10" from "2025-11-10")
                      },
                      symbolSize: 8,
                      fontSize: 11,
                    });
                    opt.grid = {
                      x: 7,
                      y: 12,
                      x2: 7,
                      y2: 0,
                      containLabel: true,
                    };
                    // Y-axis formatting logic (similar to MetricCardChartItem)
                    // Check if formatter indicates percent type
                    const isPercentType = formatter && (
                      formatter.includes('percent') ||
                      formatter.includes('Percent') ||
                      formatter === 'percent_int' ||
                      formatter.startsWith('db_percent') ||
                      formatter.startsWith('db_abs_percent')
                    );

                    if (isPercentType) {
                      // For percent types, use yAxisFormat like MetricCardChartItem does
                      opt.yAxis.axisLabel.formatter = (value: number) => yAxisFormat(value, true, 2, true);
                    } else if (trendData.metrics_name === 'arpu') {
                      // Special handling for ARPU metric
                      opt.yAxis.axisLabel.formatter = (value: any) => {
                        const formatLabel = value ? unitConversion(value, true) : value;
                        const num = value < 1 && value > 0 ? formatSmallValue(value, 'decimals', 2) : formatLabel;
                        return num;
                      };
                    } else {
                      // Default: use unitConversion for BMK formatting (B/M/K) for y-axis
                      // The formatter is used for tooltip values, not y-axis labels
                      opt.yAxis.axisLabel.formatter = (value: number) => {
                        return value ? unitConversion(value, true) : value;
                      };
                    }
                    opt.xAxis = [{
                      ...(opt.xAxis[0] || {}),
                      boundaryGap: false,
                      axisLabel: {
                        ...(opt.xAxis[0]?.axisLabel || {}),
                        fontSize: 11,
                        color: '#BBBDCB',
                      },
                    }];
                    opt.yAxis.axisLabel.fontSize = 11;
                    opt.yAxis.axisLabel.color = '#BBBDCB';
                    opt.tooltip.confine = false;
                    opt.tooltip = {
                      ...opt.tooltip,
                      appendToBody: true, // Append tooltip to body to prevent clipping by parent containers
                      axisPointer: {
                        ...opt.tooltip?.axisPointer,
                        label: {
                          ...opt.tooltip?.axisPointer?.label,
                          padding: [5, 1, 0, 1],
                          backgroundColor: 'transparent',
                        },
                      },
                    };
                    return opt;
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Wrap with drag handle container for preview and edit modes
  if (mode === 'preview' || mode === 'edit') {
    return (
      <div className="dbs-metrics-drag-handle-wrapper">
        {/* Drag handle for preview and edit modes */}
        {(mode === 'preview' || mode === 'edit') && (
          <div className="dbs-metrics-drag-handle">
            <img src={DragHandle} alt="Drag handle" />
          </div>
        )}
        {content}
      </div>
    );
  }

  return content;
};

export default MetricsBoardVertical;
